import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import Markdown from "react-markdown";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from "@/components/ui/dialog";
import { dismissReleaseNotes, getReleaseNotes } from "@/lib/api/release-notes";
import { useT } from "@/lib/i18n/use-t";
import { useRegionalFormat } from "@/lib/regional-format/context";
import { T } from "./release-notes-modal.i18n";

const ALLOWED_ELEMENTS = ["p", "strong", "em", "ul", "ol", "li", "br"];

export function ReleaseNotesModal() {
  const t = useT(T);
  const queryClient = useQueryClient();
  const { formatDate } = useRegionalFormat();
  const [open, setOpen] = useState(true);

  const { data } = useQuery({
    queryKey: ["release-notes"],
    queryFn: getReleaseNotes,
    staleTime: 3 * 60 * 60 * 1000,
    refetchInterval: 3 * 60 * 60 * 1000,
  });

  const dismiss = useMutation({
    mutationFn: dismissReleaseNotes,
    onSuccess: () => {
      queryClient.setQueryData(["release-notes"], { entries: [] });
      setOpen(false);
    },
  });

  // If a later refetch brings fresh entries (e.g. a deploy shipped new notes
  // while the tab was open), re-open the modal.
  const entryCount = data?.entries.length ?? 0;
  useEffect(() => {
    if (entryCount > 0) setOpen(true);
  }, [entryCount]);

  if (!data || data.entries.length === 0) return null;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) dismiss.mutate();
      }}
    >
      <DialogContent className="max-w-lg">
        <DialogTitle className="sr-only">{t.title}</DialogTitle>
        <DialogDescription className="sr-only">{t.description}</DialogDescription>
        <div className="relative">
          <div className="space-y-6 max-h-[55vh] overflow-y-auto pr-6 pt-8 pb-4 [scrollbar-gutter:stable] [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-muted-foreground/30">
            {data.entries.map((entry) => (
              <div key={`${entry.publishedAt}__${entry.body.slice(0, 32)}`} className="space-y-2">
                <p className="text-xs font-medium text-muted-foreground tabular-nums">
                  {formatDate(entry.publishedAt)}
                </p>
                <div className="text-sm text-foreground prose-sm leading-relaxed [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_p]:my-1 [&_li]:my-0.5">
                  <Markdown allowedElements={ALLOWED_ELEMENTS} unwrapDisallowed skipHtml>
                    {entry.body}
                  </Markdown>
                </div>
              </div>
            ))}
          </div>
          {data.entries.length > 3 && (
            <div className="pointer-events-none absolute bottom-0 left-0 right-6 h-8 bg-gradient-to-t from-background to-transparent" />
          )}
        </div>
        <DialogFooter>
          <Button type="button" onClick={() => dismiss.mutate()} loading={dismiss.isPending}>
            {t.gotIt}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
